import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { FiBold, FiImage, FiItalic, FiPaperclip, FiTrash2, FiUnderline, FiVideo } from "react-icons/fi";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import "./ContatoFlutuante.css";

const MAX_ANEXO_BYTES = 6 * 1024 * 1024;
const MAX_TOTAL_ANEXOS_BYTES = 8 * 1024 * 1024;

function formatarTamanho(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function arquivoParaBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Editor de resposta rico + anexos (imagens/videos), mesmo padrao ja usado em
// ContatoFlutuante.js (mensagem inicial), reaproveitado para as respostas do
// usuario e do GodMode nas conversas de contato.
const EditorRicoAnexos = forwardRef(function EditorRicoAnexos({ placeholder, disabled }, ref) {
  const { toast } = useFeedback();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [anexos, setAnexos] = useState([]);

  const aplicarFormato = (comando) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(comando, false, null);
  };

  const adicionarArquivos = async (arquivos) => {
    const validos = arquivos.filter((file) => file.type.startsWith("image/") || file.type.startsWith("video/"));
    if (validos.length !== arquivos.length) {
      toast.warning("Somente imagens e vídeos podem ser anexados nesta mensagem.");
    }
    if (validos.length === 0) return;

    const totalAtual = anexos.reduce((soma, anexo) => soma + anexo.tamanho, 0);
    let totalNovo = totalAtual;
    const novos = [];

    for (const file of validos) {
      if (file.size > MAX_ANEXO_BYTES) {
        toast.warning(`O arquivo ${file.name} passa de 6 MB.`);
        continue;
      }
      totalNovo += file.size;
      if (totalNovo > MAX_TOTAL_ANEXOS_BYTES) {
        toast.warning("Os anexos somados devem ter no máximo 8 MB.");
        break;
      }

      const id = `${Date.now()}-${file.name}-${file.size}-${novos.length}`;
      const conteudoBase64 = await arquivoParaBase64(file);
      novos.push({
        id,
        nome: file.name,
        tipo: file.type,
        tamanho: file.size,
        conteudo_base64: conteudoBase64,
      });
    }

    if (novos.length > 0) {
      setAnexos((atuais) => [...atuais, ...novos]);
    }
  };

  const handlePaste = async (event) => {
    const items = Array.from((event.clipboardData && event.clipboardData.items) || []);
    const arquivos = items
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (arquivos.length > 0) {
      event.preventDefault();
      await adicionarArquivos(arquivos);
      const texto = event.clipboardData && event.clipboardData.getData("text/plain");
      if (texto) document.execCommand("insertText", false, texto);
    }
  };

  const handleSelecionarArquivos = async (event) => {
    const arquivos = Array.from(event.target.files || []);
    if (arquivos.length > 0) await adicionarArquivos(arquivos);
    event.target.value = "";
  };

  const removerAnexo = (id) => {
    setAnexos((atuais) => atuais.filter((anexo) => anexo.id !== id));
  };

  useImperativeHandle(ref, () => ({
    obterConteudo() {
      const mensagemHtml = (editorRef.current && editorRef.current.innerHTML) || "";
      const mensagemTexto = (editorRef.current && editorRef.current.innerText) || "";
      return {
        mensagem_html: mensagemHtml,
        mensagem_texto: mensagemTexto,
        anexos: anexos.map(({ nome, tipo, tamanho, conteudo_base64 }) => ({ nome, tipo, tamanho, conteudo_base64 })),
      };
    },
    limpar() {
      setAnexos([]);
      if (editorRef.current) editorRef.current.innerHTML = "";
    },
  }));

  return (
    <div className="contato-editor-wrapper">
      <div className="contato-editor-toolbar" aria-label="Formatação da mensagem">
        <button type="button" onClick={() => aplicarFormato("bold")} title="Negrito" disabled={disabled}>
          <FiBold />
        </button>
        <button type="button" onClick={() => aplicarFormato("italic")} title="Itálico" disabled={disabled}>
          <FiItalic />
        </button>
        <button type="button" onClick={() => aplicarFormato("underline")} title="Sublinhado" disabled={disabled}>
          <FiUnderline />
        </button>
        <button
          type="button"
          onClick={() => {
            if (fileInputRef.current) fileInputRef.current.click();
          }}
          title="Anexar imagem ou vídeo"
          disabled={disabled}
        >
          <FiPaperclip />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="contato-file-input"
          onChange={handleSelecionarArquivos}
          disabled={disabled}
        />
      </div>
      <div
        ref={editorRef}
        className="contato-editor"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        onPaste={handlePaste}
        data-placeholder={placeholder || "Escreva sua mensagem..."}
      />

      {anexos.length > 0 && (
        <div className="contato-anexos">
          {anexos.map((anexo) => (
            <div className="contato-anexo" key={anexo.id}>
              <span className="contato-anexo-icone">
                {anexo.tipo.startsWith("image/") ? <FiImage /> : <FiVideo />}
              </span>
              <span className="contato-anexo-nome">{anexo.nome}</span>
              <span className="contato-anexo-tamanho">{formatarTamanho(anexo.tamanho)}</span>
              <button type="button" onClick={() => removerAnexo(anexo.id)} title="Remover anexo" disabled={disabled}>
                <FiTrash2 />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default EditorRicoAnexos;
